/*
 * Copyright 2021 LABOR.digital
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Last modified: 2021.08.04 at 12:47
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { setElementValue } from '@labor-digital/bits';
import { directive, EventPart } from 'lit-html';
/**
 * Directive to simplify two-way data binding on form elements.
 *
 * You simply bind it to the input event it should listen for (change/keydown for the most part)
 * and tell it which property you want to bind it to.
 *
 * The binding will then update the input value in the same way data-model would.
 *
 * You can also use @context if you would really want to.
 *
 * Example:
 * <label>
 *   Edit your two way binding<br>
 *   <input type="text" @keyup="${dataModel('data.model')}">
 * </label>
 */
export var dataModel = directive(function (property) { return function (part) { return __awaiter(void 0, void 0, void 0, function () {
    var handler, bit, prop;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                if (!(part instanceof EventPart)) {
                    throw new Error('dataModel can only be used in event bindings! Use it like: @keyup="${dataModel(\'yourProperty\')}"');
                }
                if (part.element._bitHtmlListener) {
                    return [2 /*return*/];
                }
                handler = function () { };
                part.element._bitHtmlListener = true;
                part.setValue(function (e) {
                    handler(e);
                });
                bit = part.eventContext;
                return [4 /*yield*/, bit.$context.binder.getAccessor(property)];
            case 1:
                prop = _a.sent();
                if (prop === null) {
                    return [2 /*return*/];
                }
                setElementValue(part.element, prop.get());
                handler = function (e) {
                    bit.$context.binder.reactToChangeEvent(e, part.element, prop);
                };
                return [2 /*return*/];
        }
    });
}); }; });
//# sourceMappingURL=DataModel.js.map